import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';

export interface GpsDevice {
  id: string;
  name: string;
  status: 'moving' | 'idle' | 'offline' | 'sleep' | 'unknown';
  lat: number;
  lon: number;
  speed_kmh: number;
  heading?: number;
  battery_level?: number;
  last_signal_at: string | null;
  ignition?: boolean;
  odometer?: number;
  vehicle?: {
    plate: string;
    make?: string;
    model?: string;
    category?: string;
  } | null;
  driver?: {
    id: string;
    name: string;
    cpf: string;
  } | null;
}

export const useGpsDevices = (companyId?: string) => {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ['gps-devices', companyId],
    queryFn: async (): Promise<GpsDevice[]> => {
      if (!companyId) {
        throw new Error('Company ID is required');
      }

      console.log('Fetching GPS devices for company:', companyId);

      const { data, error } = await supabase.rpc(
        'get_gps_devices_with_vehicles',
        { p_company_id: companyId }
      );

      if (error) {
        console.error('Error fetching GPS devices:', error);
        throw error;
      }

      console.log('Raw GPS devices data:', data);

      // Transform the data to match our interface
      const devices: GpsDevice[] = (data || []).map((device: any) => ({
        id: device.device_id,
        name: device.code,
        status: mapDeviceStatus(device.status, device.last_signal_at),
        lat: parseFloat(device.latitude) || 0,
        lon: parseFloat(device.longitude) || 0,
        speed_kmh: parseFloat(device.speed_kmh) || 0,
        heading: device.heading ? parseFloat(device.heading) : undefined,
        battery_level: device.battery_level,
        last_signal_at: device.last_signal_at,
        ignition: device.last_ignition,
        odometer: device.total_distance_km || 0,
        vehicle: device.vehicle_plate ? {
          plate: device.vehicle_plate,
          make: device.vehicle_make,
          model: device.vehicle_model,
          category: device.vehicle_category
        } : null,
        driver: device.driver_id ? {
          id: device.driver_id,
          name: device.driver_name,
          cpf: device.driver_cpf
        } : null
      }));

      console.log('Transformed GPS devices:', devices);
      return devices;
    },
    enabled: !!user && !!companyId,
    refetchInterval: 1000, // Refetch every 1 second for real-time updates
    staleTime: 500, // Consider data stale after 500ms
  });

  // Set up realtime subscription for GPS devices updates
  useEffect(() => {
    if (!companyId) return;

    console.log('Setting up realtime subscription for GPS devices');

    const channel = supabase
      .channel(`gps-devices-${companyId}`, {
        config: {
          broadcast: { self: false },
        },
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'fleet_gps',
          table: 'devices',
          filter: `company_id=eq.${companyId}`,
        },
        (payload) => {
          console.log('GPS device updated:', payload);
          // Invalidate and refetch the query when devices are updated
          query.refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'fleet_gps', 
          table: 'positions',
          filter: `company_id=eq.${companyId}`,
        },
        (payload) => {
          console.log('GPS position updated:', payload);
          // Invalidate and refetch when new positions arrive
          query.refetch();
        }
      )
      .subscribe((status) => {
        console.log('📡 GPS devices channel status:', status);
      });

    return () => {
      console.log('Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [companyId, query]);

  return query;
};

// Helper function to determine offline status based on last signal time
// Database status (moving/idle/sleep/unknown) is preserved unless device is truly offline
function mapDeviceStatus(
  rawStatus: string, 
  lastSignal: string | null
): 'moving' | 'idle' | 'offline' | 'sleep' | 'unknown' {
  // If no recent signal (older than 10 minutes), consider offline
  if (!lastSignal) {
    return 'offline';
  }

  const signalTime = new Date(lastSignal);
  const now = new Date();
  const timeDiff = now.getTime() - signalTime.getTime();
  const tenMinutesInMs = 10 * 60 * 1000;

  if (timeDiff > tenMinutesInMs) {
    return 'offline';
  }

  // Return the actual status from database (moving, idle, sleep, unknown)
  return rawStatus as 'moving' | 'idle' | 'offline' | 'sleep' | 'unknown';
}
